<template>
  <div>
    <div v-if="layersLoading">
      <spinner class="layers-loading" :loading="true"></spinner>
    </div>
    <div v-else-if="layers.length" class="layers-container" style="flex: 1;">
      <b-list-group class="layers-list">
        <draggable
          v-model="draggableLayers"
          handle=".handle"
          @change="dragEnded"
        >
          <b-list-group-item
            v-for="(item, index) in draggableLayers"
            :key="item.id"
            class="layers-list-group-item"
            :class="{ last: index == layers.length - 1 }"
          >
            <temporary-layer v-if="item.group == 'tymczasowa'" :layer="item">
            </temporary-layer>
            <layer
              v-else
              :layer="item"
              :isFirst="isFirstLayer(item)"
              :isLast="isLastLayer(item)"
              @activate="toggleActive(item)"
              @toggle-visible="toggleVisible(item)"
              @remove="confirmRemoval(item)"
              @set-style="setStyle"
              @create-style="createStyle(item)"
              @toggle-3d="toggle3dView(item)"
              @zoom-to-layer-range="zoomToTheLayersRange(item)"
              @remove-style="removeStyle"
            >
            </layer>
          </b-list-group-item>
        </draggable>
      </b-list-group>
    </div>
    <h4 v-else class="pt-2 pb-2 text-center text-muted">
      Brak warstw
    </h4>
    <confirmation-window ref="confirmationWindow"></confirmation-window>
    <layer-style-window ref="styleWindow"></layer-style-window>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import MapLayerStyleBuilder from '../models/MapLayerStyleBuilder';
import MapLayerBuilder from '../models/MapLayerBuilder';
import Spinner from './Spinner';
import TemporaryLayer from './TemporaryLayer';
import Layer from './Layer';
import CollapsibleWidget from './CollapsibleWidget';
import ConfirmationWindow from './ConfirmationWindow';
import LayerStyleWindow from '../components/LayerStyleWindow';
import { setLayerProperty } from '../common/layersHelper';
import draggable from 'vuedraggable';

import {
  WINDOW_DOWNLOAD_TEMPLATE_SHOW,
  WINDOW_NOTIFICATION_SHOW,
  TOGGLE_LAYER_VISIBILITY_BY_ID,
  COMPOSITION_LAYERS_EXPANDED_SET
} from '../store/mutations.type';

import {
  LAYERS_DELETE,
  STYLES_DELETE,
  TOGGLE_ACTIVE_LAYER,
  ACTIVATE_LAYER
} from '../store/actions.type';
import {Fill, Stroke, Style} from "ol/style";

export default {
  name: 'Layers',
  props: {
    accordion: {
      type: String,
      default: 'panel'
    },
    expandWidget: {
      type: Boolean,
      default: false
    },
    layers: {
      type: Array,
      default: []
    }
  },
  components: {
    CollapsibleWidget,
    Layer,
    ConfirmationWindow,
    LayerStyleWindow,
    Spinner,
    TemporaryLayer,
    draggable
  },
  data: function() {
    return {
      initValuePrefix: 'init_',
      version: ''
    };
  },
  computed: {
    ...mapGetters([
      'readOnly',
      'isHistoryDataMode',
      'dataFeedNoGeometryLayerId',
      'layersLoading',
      'getTemplateTool',
      'styles'
    ]),
    composition() {
      return this.$store.getters.composition || {};
    },
    activated() {
      return this.layers.some(x => x.active);
    },
    draggableLayers: {
      get() {
        return this.layers
          .filter(x => !x.hide)
          .sort((a, b) => {
            return b.order - a.order;
          });
      },
      set(value) {}
    }
  },
  watch: {
    'composition.loaded': function(value) {
      if (!value) {
        this.revertModifications(true);
      }
    }
  },
  methods: {
    onToggled(expanded) {
      this.$emit('toggled', expanded);
      this.$store.commit(COMPOSITION_LAYERS_EXPANDED_SET, expanded);
    },
    showConfirmation(message, yesAction, noAction) {
      let confirmationWindow = this.$refs.confirmationWindow;

      confirmationWindow.setMessage(message);
      confirmationWindow.setYesAction(yesAction);
      confirmationWindow.setNoAction(noAction);

      confirmationWindow.show();
    },
    confirmRevert(composition) {
      const self = this;

      self.showConfirmation(
        'Czy na pewno chcesz cofnąć wszystkie zmiany wprowadzone na liście warstw?',
        function() {
          self.revertModifications();
        }
      );
    },
    confirmRemoval(layer) {
      const self = this;

      self.showConfirmation(
        'Czy na pewno chcesz usunąć warstwę <b>' + layer.title + '</b>?',
        function() {
          self.$store.dispatch(LAYERS_DELETE, layer);
        }
      );
    },
    setStyle(layer, styleId) {
      setLayerProperty(layer, 'styleId', styleId);

      let style = this.styles.find(x => x.id === styleId);

      layer.mapLayer.setStyle(
        new MapLayerStyleBuilder(style, layer).getStyle()
      );

      new MapLayerBuilder(layer, style).updateOlLayer();

      layer.mapLayer.getSource().clear();
    },
    createStyle(layer) {
      this.$refs.styleWindow.setLayer(layer);
    },
    activateLayer(layer) {
      this.$store.dispatch(ACTIVATE_LAYER, layer);
    },
    isFirstLayer(layer) {
      let firstLayer = this.layers[0] || {};

      return layer.id === firstLayer.id;
    },
    isLastLayer(layer) {
      const layersWithoutTemporary = this.layers.filter(
        layer => layer.group !== 'tymczasowa'
      );
      const lastLayer =
        layersWithoutTemporary[layersWithoutTemporary.length - 1] || {};
      return layer.id === lastLayer.id;
    },
    toggleActive(layer) {
      this.$store.dispatch(TOGGLE_ACTIVE_LAYER, { layer: layer });
    },
    toggleVisible(layer) {
      this.$store.commit(TOGGLE_LAYER_VISIBILITY_BY_ID, layer.id);
    },
    toggle3dView(layer) {
      layer.view3d = !layer.view3d;

      new MapLayerBuilder(layer).updateOlLayer();
    },
    dragEnded(event) {
      const moved = event.moved;
      const movedIndexChange = moved.newIndex - moved.oldIndex;
      const draggableMoved = this.draggableLayers[moved.oldIndex];
      setLayerProperty(
        draggableMoved,
        'order',
        moved.element.order - movedIndexChange
      );

      const anotherMoved = this.draggableLayers[moved.newIndex];
      setLayerProperty(
        anotherMoved,
        'order',
        anotherMoved.order + movedIndexChange
      );

      this.applyLayersOrder();
    },
    revertModifications(skipActive) {
      let self = this;

      this.layers.forEach(x => {
        let keys = Object.keys(x).filter(key => {
          return !key.startsWith(self.initValuePrefix);
        });

        for (let i = 0; i < keys.length; ++i) {
          let key = keys[i];
          let initialKey = self.initValuePrefix + key;

          if (key !== 'visible' || !skipActive || !x.active) {
            if (x[initialKey] !== undefined) {
              x[key] = x[initialKey];
              delete x[initialKey];
            }
          }
        }

        self.setMapLayerOrder(x);
        x.mapLayer.setVisible(x.visible);

        if (x.mapHighResolutionLayer) {
          x.mapHighResolutionLayer.setVisible(x.visible);
        }

        x.modified = false;
      });
    },
    setMapLayerOrder(layer) {
      layer.mapLayer.setZIndex(layer.order);

      if (layer.mapHighResolutionLayer) {
        layer.mapHighResolutionLayer.setZIndex(layer.order);
      }
    },
    applyLayersOrder() {
      const self = this;

      self.layers.forEach(x => {
        self.setMapLayerOrder(x);
      });

      const options = {
        message: 'Kolejność warstw została zmieniona pomyślnie.',
        title: 'Sukces.',
        type: 'success',
        duration: 5000
      };

      this.$store.commit(WINDOW_NOTIFICATION_SHOW, options);
    },
    showSuccessAlert() {
      var options = {
        message: 'Zmiany zostały zapisane.',
        title: 'Sukces.',
        type: 'success',
        duration: 5000
      };

      this.$store.commit(WINDOW_NOTIFICATION_SHOW, options);
    },
    setLayerProperty(layer, propertyName, value) {
      let self = this;
      let initialPropertyName = self.initValuePrefix + propertyName;

      if (layer[initialPropertyName] === undefined) {
        layer[initialPropertyName] = layer[propertyName];
      }

      layer[propertyName] = value;

      let keys = Object.keys(layer).filter(x => {
        return !x.startsWith(self.initValuePrefix);
      });

      layer.modified = keys.some(key => {
        let initialKey = self.initValuePrefix + key;

        return (
          layer[initialKey] !== undefined && layer[initialKey] !== layer[key]
        );
      });
    },
    showDownloadTemplate() {
      this.$store.commit(WINDOW_DOWNLOAD_TEMPLATE_SHOW);
    },
    zoomToTheLayersRange(layer) {
      this.$emit('set-layer-extent', layer.extent);
    },
    removeStyle(styleId) {
      this.$store.dispatch(STYLES_DELETE, styleId);
    }
  },
  created: function() {
    this.version = '';
    // this.version = VERSION;
  }
};
</script>

<style>
.layers-list .dropdown > .btn {
  background: transparent;
  border: none;
  color: #007bff;
  padding: 3px !important;
  box-shadow: none;
}

.layers-container {
  position: relative !important;
}

.layers-list-group-item {
  border: none !important;
  padding: 0.5em !important;
}

.layers-list-group-item:hover {
  background-color: rgba(0, 0, 0, 0.05) !important;
}

.version-info {
  line-height: 40px;
  margin: auto;
}

.layers-loading {
  position: sticky;
  position: -webkit-sticky;
  bottom: 0;
  left: 50%;
}
</style>
